import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { isInitialQueryLoading } from '../../../lib/query/listQuery';
import { usePagedListUrlState } from '../../../lib/query/usePagedListUrlState';
import { useUrlListFilter } from '../../../lib/query/useUrlListFilter';
import { useUserSegmentScope } from '../../auth/hooks/useUserSegmentScope';
import { useCustomerSegmentOptions } from '../../../hooks/options/useCustomerSegmentOptions';
import { Button, Dropdown, Form, Modal, Select, Space, Typography, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { MenuProps } from 'antd';
import { DownloadOutlined, FileExcelOutlined, FilePdfOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import type { Dayjs } from 'dayjs';
import DataTable from '../../../components/DataTable';
import { ListPage, buildCogActionsColumn } from '../../../components/list';
import LeadFilterBar, { type LeadFilterValues } from './LeadFilterBar';
import LeadViewDrawer from './LeadViewDrawer';
import LeadFormDrawer from './LeadFormDrawer';
import { useLeads } from '../hooks/useLeads';
import { useUserOptions } from '../../../hooks/options/useUserOptions';
import { useCampaignOptions } from '../../../hooks/options/useCampaignOptions';
import { useChangeLeadStage } from '../hooks/useChangeLeadStage';
import { useCloseLead } from '../hooks/useCloseLead';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { appPath } from '../../../routes/paths';
import { LEAD_FILTER_FIELDS } from '../../../routes/filterMaps';
import { serializeValue } from '../../../lib/query/useUrlListFilter';

dayjs.extend(utc);
import type { LeadListItem } from '../types';
import type { ClosingStatus } from '../types';
import { useLeadPipeline } from '../hooks/useLeadPipeline';
import { PIPELINE_STAGE_LABELS, isOpenCategory } from '../constants';
import { useLeadSources } from '../hooks/useLeadSources';
import { useExportLeads } from '../hooks/useExportLeads';
import { useExportLeadsPdf } from '../hooks/useExportLeadsPdf';
import { usePermissions } from '../../permissions/PermissionsContext';
import CloseLeadModal, { type CloseLeadFormValues } from './CloseLeadModal';
import { defaultFollowUpTitle } from '../utils/followUpTitle';


const defaultFilter: LeadFilterValues = {
  search: undefined,
  pipelineStageId: undefined,
  closingStatus: undefined,
  assigneeUserId: undefined,
  contactIds: undefined,
  contactCategoryId: undefined,
  sourceId: undefined,
  campaignId: undefined,
  salesPersonId: undefined,
  createdRange: undefined,
  createdFrom: undefined,
  createdTo: undefined,
  hasReferral: undefined,
};

const LeadsPage: React.FC = () => {
  const { isScoped, assignedSegmentIds, defaultSegmentIdsForApi } = useUserSegmentScope();
  const { data: segments = [] } = useCustomerSegmentOptions({ activeOnly: true });
  const navigate = useNavigate();
  const { id: routeLeadId } = useParams<{ id: string }>();
  const { page, pageSize, setPage, setPageSize } = usePagedListUrlState(10);
  const [filter] = useUrlListFilter(defaultFilter, LEAD_FILTER_FIELDS);
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  const [formDrawerOpen, setFormDrawerOpen] = useState(false);
  const [editLeadId, setEditLeadId] = useState<string | null>(null);
  const [stageModalLeadId, setStageModalLeadId] = useState<string | null>(null);
  const [closeModalLeadId, setCloseModalLeadId] = useState<string | null>(null);
  const [stageForm] = Form.useForm<{ toStageId: string }>();
  const [closeForm] = Form.useForm<CloseLeadFormValues>();


  const segmentFilterOptions = useMemo(() => {
    if (!isScoped) {
      return segments.map((s) => ({ value: s.id, label: s.label }));
    }
    const allowed = new Set(assignedSegmentIds);
    return segments
      .filter((s) => allowed.has(s.id))
      .map((s) => ({ value: s.id, label: s.label }));
  }, [isScoped, assignedSegmentIds, segments]);

  const createdFrom = filter.createdRange?.[0]
    ? (filter.createdRange[0] as Dayjs).format?.('YYYY-MM-DD') ?? undefined
    : undefined;
  const createdTo = filter.createdRange?.[1]
    ? (filter.createdRange[1] as Dayjs).format?.('YYYY-MM-DD') ?? undefined
    : undefined;

  const listParams = useMemo(
    () => ({
      pageNumber: page,
      pageSize,
      search: filter.search || undefined,
      pipelineStageId: filter.pipelineStageId,
      closingStatus: filter.closingStatus,
      assigneeUserId: filter.assigneeUserId,
      contactIds: filter.contactIds,
      contactCategoryId: filter.contactCategoryId,
      sourceId: filter.sourceId,
      campaignId: filter.campaignId,
      referralContactId: filter.referralContactId,
      salesPersonId: filter.salesPersonId,
      createdFrom: filter.createdFrom ?? createdFrom,
      createdTo: filter.createdTo ?? createdTo,
      segmentIds: defaultSegmentIdsForApi(filter.segmentIds),
      hasReferral: filter.hasReferral,
    }),
    [page, pageSize, filter.search, filter.pipelineStageId, filter.closingStatus, filter.assigneeUserId, filter.contactIds, filter.contactCategoryId, filter.sourceId, filter.campaignId, filter.referralContactId, filter.salesPersonId, filter.segmentIds, defaultSegmentIdsForApi, filter.hasReferral, filter.createdFrom, filter.createdTo, createdFrom, createdTo]
  );

  const { data: paged, isLoading } = useLeads(listParams);
  const loadingList = isInitialQueryLoading(isLoading, paged);

  const closeModalLead = useMemo(
    () => paged?.items.find((l) => l.id === closeModalLeadId),
    [paged?.items, closeModalLeadId],
  );

  const { data: users = [], isPending: loadingUsers } = useUserOptions();
  const { data: campaigns = [] } = useCampaignOptions();
  const changeStageMutation = useChangeLeadStage();
  const closeLeadMutation = useCloseLead();
  const exportMutation = useExportLeads();
  const pdfExportMutation = useExportLeadsPdf();
  const { data: pipeline } = useLeadPipeline();
  const { data: sources } = useLeadSources();
  const { hasPermission } = usePermissions();

  useEffect(() =>
  {
    if (routeLeadId)
    {
      setSelectedLeadId(routeLeadId);
    }
  }, [routeLeadId]);

  const sourceLabelById = useMemo(() => {
    const m: Record<number, string> = {};
    (sources ?? []).forEach(s => { m[s.id] = s.label; });
    return m;
  }, [sources]);

  const stageById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const s of pipeline?.stages ?? []) {
      map[s.id] = s.label;
    }
    return map;
  }, [pipeline?.stages]);

  const stageCodeById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const s of pipeline?.stages ?? []) {
      map[s.id] = s.code;
    }
    return map;
  }, [pipeline?.stages]);

  const stageCodeByLabel = useMemo(() => {
    const map: Record<string, string> = {};
    for (const s of pipeline?.stages ?? []) {
      map[s.label] = s.code;
    }
    return map;
  }, [pipeline?.stages]);

  const openStageOptions = useMemo(
    () =>
      (pipeline?.stages ?? [])
        .filter((s) => s.isActive && isOpenCategory(s.category))
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((s) => ({ value: s.code, label: s.label })),
    [pipeline?.stages],
  );

  const defaultOpenStageCode = useMemo(() => {
    const first = (pipeline?.stages ?? [])
      .filter((s) => s.isActive && isOpenCategory(s.category))
      .sort((a, b) => a.sortOrder - b.sortOrder)[0];
    return first?.code ?? 'open';
  }, [pipeline?.stages]);

  const assigneeOptions = useMemo(
    () =>
      users.map((u) => ({
        value: u.id,
        label: u.label,
      })),
    [users]
  );
  const campaignOptions = useMemo(
    () => campaigns.map((c) => ({ value: c.id, label: c.label })),
    [campaigns]
  );

  const openStageModal = useCallback((record: LeadListItem) => {
    setStageModalLeadId(record.id);
    stageForm.setFieldsValue({
      toStageId:
        stageCodeById[String(record.pipelineStageId)] ??
        stageCodeByLabel[stageById[String(record.pipelineStageId)] ?? ''] ??
        defaultOpenStageCode,
    });
  }, [stageForm, stageCodeById, stageCodeByLabel, stageById, defaultOpenStageCode]);

  const columns: ColumnsType<LeadListItem> = useMemo(
    () => [
      {
        title: 'SN',
        key: 'sn',
        width: 56,
        fixed: 'left',
        align: 'center',
        render: (_, __, index) => (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {(page - 1) * pageSize + index + 1}
          </Typography.Text>
        ),
      },
      {
        title: 'Title',
        dataIndex: 'title',
        key: 'title',
        render: (v: string, record: LeadListItem) => (
          <Typography.Link onClick={() => setSelectedLeadId(record.id)}>
            {v}
          </Typography.Link>
        ),
      },
      {
        title: 'Contact',
        dataIndex: 'contactName',
        key: 'contactName',
        render: (v: string | null) => v ?? '-',
      },
      {
        title: 'Referral Agent',
        dataIndex: 'referralContactName',
        key: 'referralContactName',
        render: (v: string | null) => v ?? '-',
      },
      {
        title: 'Sales Person',
        dataIndex: 'salesPersonName',
        key: 'salesPersonName',
        render: (v: string | null) => v ?? '-',
      },
      {
        title: 'Assignee',
        dataIndex: 'assigneeDisplayName',
        key: 'assigneeDisplayName',
        render: (v: string | null) => v ?? '-',
      },
      {
        title: 'Stage',
        dataIndex: 'pipelineStage',
        key: 'pipelineStage',
        render: (_: unknown, record: LeadListItem) => (
          <Tag color={record.closingStatus !== 0 ? 'default' : 'blue'}>
            {stageById[record.pipelineStageId] ??
              PIPELINE_STAGE_LABELS[record.pipelineStage as keyof typeof PIPELINE_STAGE_LABELS] ??
              record.pipelineStageId}
          </Tag>
        ),
      },
      {
        title: 'Source',
        dataIndex: 'sourceId',
        key: 'sourceId',
        render: (v: number) => sourceLabelById[v] ?? '-',
      },
      {
        title: 'Campaign',
        dataIndex: 'campaignName',
        key: 'campaignName',
        render: (v: string | null) => v ?? '-',
      },
      {
        title: 'Targeted',
        dataIndex: 'targetedAmount',
        key: 'targetedAmount',
        align: 'right',
        render: (v: number) => v.toLocaleString('en-US', { minimumFractionDigits: 2 }),
      },
      {
        title: 'Closed',
        dataIndex: 'closedAmount',
        key: 'closedAmount',
        align: 'right',
        render: (v: number | null) => v != null ? v.toLocaleString('en-US', { minimumFractionDigits: 2 }) : '-',
      },
      {
        title: 'System Price',
        dataIndex: 'systemPrice',
        key: 'systemPrice',
        align: 'right',
        render: (v: number) => v.toLocaleString('en-US', { minimumFractionDigits: 2 }),
      },
      buildCogActionsColumn<LeadListItem>({
        onView: (record) => setSelectedLeadId(record.id),
        onEdit: (record) => {
          setEditLeadId(record.id);
          setFormDrawerOpen(true);
        },
        extraItems: (record) => [
          {
            key: 'stage',
            label: 'Move Stage',
            onClick: () => openStageModal(record),
          },
          {
            key: 'close',
            label: 'Close Lead',
            onClick: () => {
              setCloseModalLeadId(record.id);
              closeForm.resetFields();
              closeForm.setFieldsValue({
                closingStatus: 2 as ClosingStatus,
                scheduleNext: true,
                nextTitle: defaultFollowUpTitle(record.title),
              });
            },
          },
        ],
        rowNumber: (_record, index) => (page - 1) * pageSize + index + 1,
      }),
    ],
    [stageById, sourceLabelById, openStageModal, closeForm, page, pageSize],
  );

  const handlePaginationChange = useCallback((newPage: number, newPageSize: number) => {
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
    } else {
      setPage(newPage);
    }
  }, [pageSize, setPage, setPageSize]);

  const [, rawSetSearchParams] = useSearchParams();

  const handleFilterChange = useCallback((next: LeadFilterValues) => {
    const prepared = { ...next };
    if (next.createdRange !== undefined) {
      if (next.createdRange?.[0] && next.createdRange[1]) {
        prepared.createdFrom = dayjs(next.createdRange[0]).format('YYYY-MM-DD');
        prepared.createdTo = dayjs(next.createdRange[1]).format('YYYY-MM-DD');
      } else {
        prepared.createdFrom = undefined;
        prepared.createdTo = undefined;
      }
    }
    rawSetSearchParams(
      (prev) => {
        const nextParams = new URLSearchParams(prev);
        nextParams.delete('page');
        for (const def of LEAD_FILTER_FIELDS) {
          const val = (prepared as Record<string, unknown>)[def.field];
          const serialized = serializeValue(val, def);
          if (serialized !== undefined) {
            nextParams.set(def.param, serialized);
          } else {
            nextParams.delete(def.param);
          }
        }
        return nextParams;
      },
      { replace: true },
    );
  }, [rawSetSearchParams]);

  const handleFilterReset = useCallback(() => {
    rawSetSearchParams(
      (prev) => {
        const nextParams = new URLSearchParams(prev);
        nextParams.delete('page');
        for (const def of LEAD_FILTER_FIELDS) {
          nextParams.delete(def.param);
        }
        return nextParams;
      },
      { replace: true },
    );
  }, [rawSetSearchParams]);

  const handleExport = useCallback((format: 'excel' | 'pdf') => {
    const exportParams: import('../types').ExportLeadsParams = {
      search: filter.search || undefined,
      pipelineStageId: filter.pipelineStageId,
      closingStatus: filter.closingStatus,
      assigneeUserId: filter.assigneeUserId,
      contactIds: filter.contactIds,
      contactCategoryId: filter.contactCategoryId,
      sourceId: filter.sourceId,
      campaignId: filter.campaignId,
      referralContactId: filter.referralContactId,
      salesPersonId: filter.salesPersonId,
      createdFrom: filter.createdFrom ?? createdFrom,
      createdTo: filter.createdTo ?? createdTo,
      segmentIds: defaultSegmentIdsForApi(filter.segmentIds),
      hasReferral: filter.hasReferral,
    };
    if (format === 'excel') {
      exportMutation.mutate(exportParams);
    } else {
      pdfExportMutation.mutate(exportParams);
    }
  }, [filter, defaultSegmentIdsForApi, exportMutation, pdfExportMutation, createdFrom, createdTo]);

  const exportMenuItems: MenuProps['items'] = [
    { key: 'excel', icon: <FileExcelOutlined />, label: 'Excel (.xlsx)' },
    { key: 'pdf', icon: <FilePdfOutlined />, label: 'PDF (.pdf)' },
  ];

  const handleStageOk = () => {
    stageForm.validateFields().then((values) => {
      if (stageModalLeadId) {
        changeStageMutation.mutate(
          { leadId: stageModalLeadId, toStageId: values.toStageId },
          {
            onSuccess: () => {
              setStageModalLeadId(null);
              stageForm.resetFields();
            },
          }
        );
      }
    });
  };

  const handleCloseOk = () => {
    closeForm.validateFields().then((values) => {
      if (closeModalLeadId) {
        closeLeadMutation.mutate(
          {
            leadId: closeModalLeadId,
            payload: {
              closingStatus: values.closingStatus!,
              closedAmount: values.closedAmount,
              actualClosingDate: values.actualClosingDate?.utc(true).startOf('day').toISOString(),
              closingNote: values.closingNote,
              scheduleNext: values.scheduleNext ?? false,
              nextDueDate: values.scheduleNext && values.nextDueDate
                ? values.nextDueDate.format('YYYY-MM-DD')
                : undefined,
              nextTitle: values.scheduleNext ? values.nextTitle : undefined,
              nextDescription: values.scheduleNext ? values.nextDescription : undefined,
              nextPriority: values.scheduleNext ? values.nextPriority : undefined,
            },
          },
          {
            onSuccess: () => {
              setCloseModalLeadId(null);
              closeForm.resetFields();
            },
          }
        );
      }
    });
  };

  return (
    <>
      <ListPage
        title="Leads"
        subtitle="Track pipeline stages, assignees, and closing status across your sales funnel."
        actions={
          <Space>
            {hasPermission('leads.view') && (
              <Dropdown
                menu={{ items: exportMenuItems, onClick: ({ key }) => handleExport(key as 'excel' | 'pdf') }}
                trigger={['click']}
              >
                <Button
                  icon={<DownloadOutlined />}
                  loading={exportMutation.isPending || pdfExportMutation.isPending}
                >
                  Export
                </Button>
              </Dropdown>
            )}
            {hasPermission('leads.create') && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditLeadId(null); setFormDrawerOpen(true); }}>
                Add Lead
              </Button>
            )}
          </Space>
        }
        filters={(
          <LeadFilterBar
            value={filter}
            onChange={handleFilterChange}
            onReset={handleFilterReset}
            assigneeOptions={assigneeOptions}
            campaignOptions={campaignOptions}
            segmentOptions={segmentFilterOptions}
            loadingAssignees={loadingUsers}
          />
        )}
      >
        <DataTable<LeadListItem>
          columns={columns}
          dataSource={paged?.items ?? []}
          loading={loadingList}
          rowKey="id"
          bordered
          striped
          scroll={{ x: 'max-content' }}
          pagination={
            paged
              ? {
                  current: paged.pageNumber,
                  pageSize: paged.pageSize,
                  total: paged.totalCount,
                  onChange: handlePaginationChange,
                }
              : undefined
          }
          emptyText="No leads yet. Add your first lead."
        />
      </ListPage>

      <LeadViewDrawer
        open={selectedLeadId != null}
        onClose={() => {
          setSelectedLeadId(null);
          if (routeLeadId)
          {
            navigate(appPath('/leads'), { replace: true });
          }
        }}
        leadId={selectedLeadId}
        onEdit={(id) => {
          setEditLeadId(id);
          setFormDrawerOpen(true);
          setSelectedLeadId(null);
        }}
      />

      <LeadFormDrawer
        open={formDrawerOpen}
        onClose={() => {
          setFormDrawerOpen(false);
          setEditLeadId(null);
        }}
        onSuccess={() => setFormDrawerOpen(false)}
        editLeadId={editLeadId}
      />

      <Modal
        title="Move Stage"
        open={stageModalLeadId != null}
        onOk={handleStageOk}
        onCancel={() => setStageModalLeadId(null)}
        confirmLoading={changeStageMutation.isPending}
      >
        <div className="cp-form-section">
          <Form form={stageForm} layout="vertical">
            <Form.Item name="toStageId" label="New Stage" rules={[{ required: true }]}>
              <Select<string> options={openStageOptions} />
            </Form.Item>
          </Form>
        </div>
      </Modal>

      <CloseLeadModal
        key={closeModalLeadId ?? 'close-lead'}
        open={closeModalLeadId != null}
        form={closeForm}
        leadTitle={closeModalLead?.title}
        onOk={handleCloseOk}
        onCancel={() => {
          setCloseModalLeadId(null);
          closeForm.resetFields();
        }}
        confirmLoading={closeLeadMutation.isPending}
      />
    </>
  );
};

export default LeadsPage;
